import React, { useEffect, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { World, WorldAdminSettings } from '../../types';
import { Note, Section, Toggle } from '../ui/primitives';

interface Props {
  world: World;
  /** A setting was saved; the parent should refetch the worlds list so the hero pill follows. */
  onWorldChanged?: () => void;
}

type AdminToggleKey = 'autoShutdownDisabled' | 'ownerAbsentShutdownDisabled';

/** One row per admin toggle. Add a key here (and to WorldAdminSettings) when the backend grows another one. */
const ADMIN_RULES: { key: AdminToggleKey; label: string; help: string }[] = [
  {
    key: 'autoShutdownDisabled',
    label: 'Disable auto shutdown',
    help: 'The server never stops itself: not when it is empty, not when the owner leaves. Stop it from the power controls or in game.',
  },
  {
    key: 'ownerAbsentShutdownDisabled',
    label: 'Disable owner leave shutdown',
    help: 'The owner can leave without starting a countdown. Trusted players keep the world loaded; the shutdown only starts once the last player leaves.',
  },
];

/**
 * Overworld height limits, same as World.java on the backend: the bottom stays at -64, the top is the last
 * block of a height that is a multiple of 16, and the dimension may not reach above Y 2031.
 */
const MIN_Y = -64;
const DEFAULT_TOP_Y = 319;
const MIN_TOP_Y = 255;
const MAX_TOP_Y = 2031;
/** Rounds a requested top Y up to the next one a dimension type can express: 1000 becomes 1007. */
const normalizeTopY = (topY: number): number => {
  const blocks = topY + 1 - MIN_Y;
  return MIN_Y + Math.ceil(blocks / 16) * 16 - 1;
};

const fromWorld = (world: World): WorldAdminSettings => ({
  autoShutdownDisabled: !!world.autoShutdownDisabled,
  ownerAbsentShutdownDisabled: !!world.ownerAbsentShutdownDisabled,
  maxBuildHeight: world.maxBuildHeight ?? DEFAULT_TOP_Y,
});

/** Admin / developer only: per-world overrides. */
const AdminTab: React.FC<Props> = ({ world, onWorldChanged }) => {
  const [settings, setSettings] = useState<WorldAdminSettings>(() => fromWorld(world));
  const [heightInput, setHeightInput] = useState(() => String(fromWorld(world).maxBuildHeight));
  const [saving, setSaving] = useState<keyof WorldAdminSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [heightError, setHeightError] = useState<string | null>(null);

  // The worlds list is the source of truth; follow it whenever it is refetched.
  useEffect(() => setSettings(fromWorld(world)), [world]);
  // Keyed on the value, not the world object, so the 15 s poll does not wipe a half-typed height.
  useEffect(() => setHeightInput(String(world.maxBuildHeight ?? DEFAULT_TOP_Y)), [world.id, world.maxBuildHeight]);

  const toggle = async (key: AdminToggleKey, value: boolean) => {
    const previous = settings;
    setSettings({ ...settings, [key]: value });
    setSaving(key);
    setError(null);
    try {
      const updated = await Worlds.updateAdminSettings(world.id, { [key]: value });
      setSettings(fromWorld(updated));
      onWorldChanged && onWorldChanged();
    } catch (e) {
      setSettings(previous);
      setError(errorMessage(e, 'Failed to save the admin setting.'));
    } finally {
      setSaving(null);
    }
  };

  const typed = /^\d+$/.test(heightInput.trim()) ? Number(heightInput.trim()) : NaN;
  const heightValid = typed >= MIN_TOP_Y && typed <= MAX_TOP_Y;
  const topY = heightValid ? normalizeTopY(typed) : NaN;
  const heightDirty = heightValid && topY !== settings.maxBuildHeight;
  const lowering = heightDirty && topY < settings.maxBuildHeight;

  const saveHeight = async () => {
    if (!heightDirty || saving) return;
    setSaving('maxBuildHeight');
    setHeightError(null);
    try {
      const updated = await Worlds.updateAdminSettings(world.id, { maxBuildHeight: topY });
      setSettings(fromWorld(updated));
      setHeightInput(String(fromWorld(updated).maxBuildHeight));
      onWorldChanged && onWorldChanged();
    } catch (e) {
      setHeightError(errorMessage(e, 'Failed to save the max build height.'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <Section
        title="Server"
        blurb="The cloud instance this world runs on. It is recorded the moment a server is claimed or created for the world and cleared once that server has been deleted, so it can be checked against the provider console."
      >
        {world.serverId ? (
          <div className="rules">
            <div className="rule">
              <span>
                <b>Instance id</b>
                <small>{world.serverId}</small>
              </span>
            </div>
            <div className="rule">
              <span>
                <b>Public IP</b>
                <small>{world.serverIp ?? 'not known yet'}</small>
              </span>
            </div>
          </div>
        ) : (
          <Note>No server is recorded. The world is offline, or it was started before servers were recorded and will be from its next start.</Note>
        )}
      </Section>
      <Section title="Automatic shutdown" blurb="Overrides for this world only. Changes apply immediately, including to a running server.">
        {error && <Note tone="danger">{error}</Note>}
        <Note>
          Normally a world shuts down after a while with nobody online, or after the owner has left while others stay. Forever worlds turn
          off auto shutdown altogether; collab worlds keep it but skip the owner-leave countdown.
        </Note>
        <div className="rules">
          {ADMIN_RULES.map((r) => (
            <div className="rule" key={r.key}>
              <span>
                <b>{r.label}</b>
                <small>{r.help}</small>
              </span>
              <Toggle checked={settings[r.key]} disabled={saving === r.key} onChange={(v) => toggle(r.key, v)} label={r.label} />
            </div>
          ))}
        </div>
      </Section>
      <Section
        title="World height"
        blurb="Applies the next time the world is started. A running server keeps its current height until it is stopped and started again."
      >
        {heightError && <Note tone="danger">{heightError}</Note>}
        {lowering && (
          <Note tone="warn">Lowering removes every block above Y {topY} the next time the world starts. Take a backup first.</Note>
        )}
        <div className="field">
          <label htmlFor="admin-max-height">Max build height</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              id="admin-max-height"
              type="number"
              className="input input--mono"
              min={MIN_TOP_Y}
              max={MAX_TOP_Y}
              value={heightInput}
              onChange={(e) => {
                setHeightInput(e.target.value);
                setHeightError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && saveHeight()}
              style={{ width: 140 }}
              disabled={saving === 'maxBuildHeight'}
            />
            <button type="button" className="btn btn--primary" onClick={saveHeight} disabled={!heightDirty || saving === 'maxBuildHeight'}>
              {saving === 'maxBuildHeight' && <span className="spin" style={{ borderTopColor: '#fff' }} />} Save
            </button>
            {!heightValid && (
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                A whole number from {MIN_TOP_Y} to {MAX_TOP_Y}.
              </span>
            )}
            {heightValid && topY !== typed && (
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Will be saved as {topY}.</span>
            )}
          </div>
          <small style={{ fontSize: 11.5, color: 'var(--ink3)' }}>
            The highest Y players can build at. Vanilla is {DEFAULT_TOP_Y}; the bottom stays at {MIN_Y}. Values round up to the next one
            Minecraft can express (steps of 16).
          </small>
        </div>
      </Section>
    </>
  );
};

export default AdminTab;
